import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAdminAuth } from './AdminAuthContext';
import { adminAPI } from './adminAPI';
import { ConversationSummary, ConversationStats, ConversationDetail, AISummary } from './admin-types';
import './AdminDashboard.css';

const AdminDashboard: React.FC = () => {
  const { logout } = useAdminAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState<ConversationStats | null>(null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null);
  const [summary, setSummary] = useState<AISummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    loadData();
  }, [currentPage]); // Reload when page changes

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statsData, conversationsData] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getConversations(currentPage, ITEMS_PER_PAGE),
      ]);
      setStats(statsData);
      setConversations(conversationsData.conversations);
      setTotalPages(conversationsData.pagination.totalPages);
      setHasNext(conversationsData.pagination.hasNext);
      setHasPrev(conversationsData.pagination.hasPrev);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleViewConversation = async (sessionId: string) => {
    try {
      const detail = await adminAPI.getConversationDetail(sessionId);
      setSelectedConversation(detail);
      
      // Check if this conversation has a saved summary
      if (detail.aiSummary) {
        setSummary({
          sessionId: detail.sessionId,
          summary: detail.aiSummary,
          conversationLength: detail.history.length,
          language: detail.language,
          createdAt: detail.createdAt,
          summarizedAt: detail.summarizedAt ? new Date(detail.summarizedAt).toISOString() : undefined,
          cached: true
        });
      } else {
        setSummary(null);
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const handleSummarize = async (force = false) => {
    if (!selectedConversation) return;
    
    setIsSummarizing(true);
    try {
      const summaryData = await adminAPI.summarizeConversation(selectedConversation.sessionId, force);
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to summarize:', err);
    } finally {
      setIsSummarizing(false);
    }
  };

  const closeModal = () => {
    setSelectedConversation(null);
    setSummary(null);
  };

  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <div>
            <h1>IT Yorozuya Admin Dashboard</h1>
            <p>Conversation Management & Analytics</p>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-container">
        {error && (
          <div className="error-banner">
            {error}
            <button onClick={loadData}>Retry</button>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Conversations</h3>
            <div className="stat-value">{stats?.totalConversations || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Total Messages</h3>
            <div className="stat-value">{stats?.totalMessages || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Japanese 🇯🇵</h3>
            <div className="stat-value">{stats?.languageBreakdown?.ja || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Vietnamese 🇻🇳</h3>
            <div className="stat-value">{stats?.languageBreakdown?.vi || 0}</div>
          </div>
        </div>

        <div className="conversations-section">
          <div className="section-header">
            <h2>Recent Conversations</h2>
            <button className="refresh-button" onClick={loadData}>
              🔄 Refresh
            </button>
          </div>

          {conversations.length === 0 ? (
            <div className="empty-state">
              <p>No conversations yet</p>
            </div>
          ) : (
            <div className="conversation-list">
              {conversations.map((conv) => (
                <div
                  key={conv.sessionId}
                  className="conversation-item"
                  onClick={() => handleViewConversation(conv.sessionId)}
                >
                  <div className="conversation-header">
                    <div>
                      <span className={`language-badge ${conv.language}`}>
                        {conv.language.toUpperCase()}
                      </span>
                      <span className="conversation-id">{conv.sessionId}</span>
                      {conv.hasSummary && (
                        <span className="summary-indicator" title="Has AI summary">
                          💾
                        </span>
                      )}
                    </div>
                    <div className="conversation-meta">
                      <span>💬 {conv.messageCount} messages</span>
                      <span>🕒 {new Date(conv.lastAccessedAt).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="conversation-preview">
                    {conv.firstMessage}...
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Conversation Detail Modal */}
      {selectedConversation && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Conversation Details</h2>
              <button className="close-button" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="conversation-info">
                <p><strong>Session ID:</strong> <code>{selectedConversation.sessionId}</code></p>
                <p>
                  <strong>Language:</strong>{' '}
                  <span className={`language-badge ${selectedConversation.language}`}>
                    {selectedConversation.language.toUpperCase()}
                  </span>
                </p>
                <p><strong>Created:</strong> {new Date(selectedConversation.createdAt).toLocaleString()}</p>
                <p><strong>Messages:</strong> {selectedConversation.history.length}</p>
              </div>

              <div className="message-list">
                {selectedConversation.history.map((msg, idx) => (
                  <div key={idx} className={`message ${msg.role}`}>
                    <div className="message-role">
                      {msg.role === 'user' ? 'Customer' : 'AI Assistant'}
                    </div>
                    <div className="message-content">{msg.content}</div>
                    {msg.timestamp && (
                      <div className="message-time">
                        {new Date(msg.timestamp).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {!summary ? (
                <button
                  className="summarize-button"
                  onClick={() => handleSummarize(false)}
                  disabled={isSummarizing}
                >
                  {isSummarizing ? '⏳ Generating...' : '🤖 AI Summary & Action Plan'}
                </button>
              ) : (
                <div className="summary-actions">
                  <button
                    className="summarize-button regenerate"
                    onClick={() => handleSummarize(true)}
                    disabled={isSummarizing}
                  >
                    {isSummarizing ? '⏳ Regenerating...' : '🔄 Regenerate Summary'}
                  </button>
                  {summary.cached && summary.summarizedAt && (
                    <small className="summary-cached-info">
                      💾 Cached summary from {new Date(summary.summarizedAt).toLocaleString()}
                    </small>
                  )}
                </div>
              )}

              {summary && (
                <div className="summary-section">
                  <h3>
                    AI Analysis & Action Plan
                    {summary.cached && <span className="cached-badge">💾 Cached</span>}
                  </h3>
                  <div className="summary-content">
                    <ReactMarkdown>{summary.summary}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {conversations.length > 0 && (
        <div className="pagination-controls">
          <button 
            className="pagination-button"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={!hasPrev || isLoading}
          >
            ← Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            className="pagination-button"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={!hasNext || isLoading}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
